import { Injectable } from "@angular/core";
import { InvoiceType } from "../models/Dte/invoiceType.enum";
import { EstadoBorradorDte } from "../models/Dte/estado-borrador-dte";
import { LocalStorageService } from "./local-storage.service";

interface ContenedorBorradores {
	[invoiceType: number]: EstadoBorradorDte;
}

interface CargaBorradorCifrada {
	iv: string;
	data: string;
}

@Injectable({
	providedIn: "root",
})
export class BorradorDteService {
	private textEncoder = new TextEncoder();
	private textDecoder = new TextDecoder();

	constructor(private localStorage: LocalStorageService) {}

	async guardarBorrador(state: EstadoBorradorDte, userId: string, emisorId: string): Promise<void> {
		const borradores = await this.obtenerMapaBorradores(userId, emisorId);
		borradores[state.invoiceType] = state;
		const serializado = await this.serializarBorradores(borradores, userId, emisorId);
		this.localStorage.guardarContenedorBorradoresDte(userId, emisorId, serializado);
	}

	async obtenerBorrador(userId: string, emisorId: string, invoiceType: InvoiceType): Promise<EstadoBorradorDte | null> {
		const borradores = await this.obtenerMapaBorradores(userId, emisorId);
		const result = borradores[invoiceType] ?? null;
		return result;
	}

	async limpiarBorrador(userId: string, emisorId: string, invoiceType: InvoiceType): Promise<void> {
		const borradores = await this.obtenerMapaBorradores(userId, emisorId);
		if (borradores[invoiceType]) {
			delete borradores[invoiceType];
			if (Object.keys(borradores).length === 0) {
				this.localStorage.eliminarContenedorBorradoresDte(userId, emisorId);
			} else {
				const serializado = await this.serializarBorradores(borradores, userId, emisorId);
				this.localStorage.guardarContenedorBorradoresDte(userId, emisorId, serializado);
			}
		}
	}

	private async obtenerMapaBorradores(userId: string, emisorId: string): Promise<ContenedorBorradores> {
		const valorAlmacenado = this.localStorage.obtenerContenedorBorradoresDte(userId, emisorId);
		if (!valorAlmacenado) {
			return {};
		}

		try {
			const parseado = JSON.parse(valorAlmacenado) as ContenedorBorradores | CargaBorradorCifrada;
			if (this.esCargaCifrada(parseado)) {
				const descifrado = await this.descifrarCarga(parseado, userId, emisorId);
				return (JSON.parse(descifrado) as ContenedorBorradores) ?? {};
			}
			if (!window.crypto?.subtle) {
				this.localStorage.eliminarContenedorBorradoresDte(userId, emisorId);
				return {};
			}
			// Migra una carga antigua en texto plano al formato cifrado.
			const borradores = (parseado as ContenedorBorradores) ?? {};
			const serializado = await this.serializarBorradores(borradores, userId, emisorId);
			this.localStorage.guardarContenedorBorradoresDte(userId, emisorId, serializado);
			return borradores;
		} catch (error) {
			try {
				const descifrado = await this.descifrarCadena(valorAlmacenado, userId, emisorId);
				return (JSON.parse(descifrado) as ContenedorBorradores) ?? {};
			} catch (decryptError) {
				this.localStorage.eliminarContenedorBorradoresDte(userId, emisorId);
				return {};
			}
		}
	}

	private async serializarBorradores(borradores: ContenedorBorradores, userId: string, emisorId: string): Promise<string> {
		const textoPlano = JSON.stringify(borradores);
		if (!window.crypto?.subtle) {
			throw new Error("El navegador no dispone de cifrado seguro para guardar borradores");
		}
		const cargaCifrada = await this.cifrarCadena(textoPlano, userId, emisorId);
		return JSON.stringify(cargaCifrada);
	}

	private async cifrarCadena(value: string, userId: string, emisorId: string): Promise<CargaBorradorCifrada> {
		const llave = await this.obtenerLlaveCifrado(userId, emisorId);
		const iv = window.crypto.getRandomValues(new Uint8Array(12));
		const bufferCifrado = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, llave, this.textEncoder.encode(value));
		return {
			iv: this.arrayBufferABase64(iv.buffer),
			data: this.arrayBufferABase64(bufferCifrado),
		};
	}

	private async descifrarCarga(payload: CargaBorradorCifrada, userId: string, emisorId: string): Promise<string> {
		if (!window.crypto?.subtle) {
			throw new Error("El navegador no dispone de cifrado seguro para leer borradores");
		}
		const llave = await this.obtenerLlaveCifrado(userId, emisorId);
		const descifrado = await window.crypto.subtle.decrypt(
			{
				name: "AES-GCM",
				iv: new Uint8Array(this.base64AArrayBuffer(payload.iv)),
			},
			llave,
			this.base64AArrayBuffer(payload.data)
		);
		return this.textDecoder.decode(descifrado);
	}

	private async descifrarCadena(value: string, userId: string, emisorId: string): Promise<string> {
		if (!window.crypto?.subtle) {
			throw new Error("El navegador no dispone de cifrado seguro para leer borradores");
		}
		const parseado = JSON.parse(value) as CargaBorradorCifrada;
		if (!this.esCargaCifrada(parseado)) {
			return value;
		}
		return this.descifrarCarga(parseado, userId, emisorId);
	}

	private async obtenerLlaveCifrado(userId: string, emisorId: string): Promise<CryptoKey> {
		const materialLlave = await window.crypto.subtle.importKey("raw", this.textEncoder.encode(userId), { name: "PBKDF2" }, false, ["deriveKey"]);
		return window.crypto.subtle.deriveKey(
			{
				name: "PBKDF2",
				salt: this.textEncoder.encode(emisorId),
				iterations: 100000,
				hash: "SHA-256",
			},
			materialLlave,
			{ name: "AES-GCM", length: 256 },
			false,
			["encrypt", "decrypt"]
		);
	}

	private esCargaCifrada(value: ContenedorBorradores | CargaBorradorCifrada): value is CargaBorradorCifrada {
		return value && typeof value === "object" && "iv" in value && "data" in value;
	}

	private arrayBufferABase64(buffer: ArrayBuffer): string {
		let binario = "";
		const bytes = new Uint8Array(buffer);
		const largo = bytes.byteLength;
		for (let i = 0; i < largo; i++) {
			binario += String.fromCharCode(bytes[i]);
		}
		return window.btoa(binario);
	}

	private base64AArrayBuffer(base64: string): ArrayBuffer {
		const cadenaBinaria = window.atob(base64);
		const largo = cadenaBinaria.length;
		const bytes = new Uint8Array(largo);
		for (let i = 0; i < largo; i++) {
			bytes[i] = cadenaBinaria.charCodeAt(i);
		}
		return bytes.buffer;
	}
}
